import React, { useEffect, useState } from 'react';
import { Save, RefreshCw, Globe, GripVertical, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Field-level checkbox with a tight label. */
const FieldToggle = ({ checked, onChange, label, testid }) => (
  <label className="inline-flex items-center gap-1.5 cursor-pointer text-[10px] uppercase tracking-[0.12em] text-white/55">
    <input type="checkbox" checked={!!checked} onChange={onChange} className="accent-[#D4AF37] w-3.5 h-3.5" data-testid={testid} />
    <span className={checked ? 'text-white/80' : 'text-white/40'}>{label}</span>
  </label>
);

export const GlobalFootprintTab = ({ getStoredPassword }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    let next = null;
    try {
      const pwd = encodeURIComponent(getStoredPassword?.() || '');
      const r = await fetch(`${API_URL}/api/admin/global-footprint?password=${pwd}`);
      if (r.ok) {
        next = await r.json();
        next.regions = (next.regions || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      }
    } catch { toast.error('Failed to load global footprint'); }
    if (next) setData(next);
    setLoading(false);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      const pwd = encodeURIComponent(getStoredPassword?.() || '');
      try {
        const r = await fetch(`${API_URL}/api/admin/global-footprint?password=${pwd}`);
        if (!r.ok || !alive) return;
        const d = await r.json();
        d.regions = (d.regions || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        if (alive) {
          setData(d);
          setLoading(false);
        }
      } catch {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [getStoredPassword]);

  const onSave = async () => {
    setSaving(true);
    try {
      const pwd = encodeURIComponent(getStoredPassword?.() || '');
      const r = await fetch(`${API_URL}/api/admin/global-footprint?password=${pwd}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: data.enabled,
          title: data.title || '',
          subtitle: data.subtitle || '',
          regions: (data.regions || []).map((r, i) => ({ ...r, order: i + 1 })),
        }),
      });
      if (r.ok) {
        toast.success('Global Footprint saved');
        await load();
      } else {
        toast.error('Save failed');
      }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const patchRegion = (idx, patch) => {
    setData(p => ({
      ...p, regions: p.regions.map((r, i) => i === idx ? { ...r, ...patch } : r),
    }));
  };

  const move = (idx, dir) => {
    setData(p => {
      const arr = [...p.regions];
      const tgt = idx + dir;
      if (tgt < 0 || tgt >= arr.length) return p;
      [arr[idx], arr[tgt]] = [arr[tgt], arr[idx]];
      return { ...p, regions: arr };
    });
  };

  const addRegion = () => {
    setData(p => ({
      ...p,
      regions: [...(p.regions || []), {
        id: `region-${Date.now().toString(36)}`,
        label: 'New Region',
        subtitle: '', phone: '', email: '',
        show_subtitle: true, show_phone: false, show_email: false,
        enabled: true, order: (p.regions?.length || 0) + 1,
      }],
    }));
  };

  const removeRegion = (idx) => {
    setData(p => ({ ...p, regions: p.regions.filter((_, i) => i !== idx) }));
  };

  if (loading || !data) {
    return <div className="text-white/50 text-sm py-10 text-center">Loading…</div>;
  }

  return (
    <div className="space-y-5" data-testid="global-footprint-tab">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1">Footer Card</div>
          <h2 className="text-xl font-semibold text-white">Global Footprint</h2>
          <p className="text-[12px] text-white/50 mt-1 max-w-2xl">
            Multi-region presence card shown above the footer. Master toggle hides the whole
            card; per-region toggle hides a single region; per-field toggles let you keep a
            region visible while hiding (e.g.) its phone number.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 cursor-pointer" data-testid="footprint-master-toggle">
            <input type="checkbox" checked={!!data.enabled} onChange={(e) => setData(p => ({ ...p, enabled: e.target.checked }))} className="accent-[#D4AF37] w-4 h-4" />
            <span className={`text-[11px] uppercase tracking-[0.15em] font-semibold ${data.enabled ? 'text-[#D4AF37]' : 'text-white/40'}`}>
              {data.enabled ? 'Visible' : 'Hidden'}
            </span>
          </label>
          <button onClick={load} disabled={loading} data-testid="footprint-refresh" className="px-3 py-1.5 rounded-md border border-white/10 hover:border-[#D4AF37]/60 text-white text-[12px] flex items-center gap-1.5 transition">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <Button onClick={onSave} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" data-testid="footprint-save">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      {/* Title + subtitle */}
      <div className="bg-white/[0.03] border border-white/10 rounded-lg p-5 grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-white/40 block mb-1.5">Card Title</label>
          <input value={data.title || ''} onChange={(e) => setData(p => ({ ...p, title: e.target.value }))}
            className="w-full bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/60"
            data-testid="footprint-title-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-white/40 block mb-1.5">Card Subtitle</label>
          <input value={data.subtitle || ''} onChange={(e) => setData(p => ({ ...p, subtitle: e.target.value }))}
            placeholder="Short caption shown under the title"
            className="w-full bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/60"
            data-testid="footprint-subtitle-input" />
        </div>
      </div>

      {/* Regions */}
      <div className="bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/50">Regions</div>
          <button onClick={addRegion} className="text-[11px] text-[#D4AF37] hover:text-[#E8C547] flex items-center gap-1" data-testid="footprint-add-region">
            <Plus className="w-3.5 h-3.5" /> Add region
          </button>
        </div>
        <div className="divide-y divide-white/5">
          {data.regions.map((r, idx) => (
            <div key={r.id || idx} className="p-4 space-y-3" data-testid={`footprint-region-row-${r.id}`}>
              {/* Row 1: order arrows + enable + label + delete */}
              <div className="flex items-center gap-3">
                <div className="flex flex-col text-white/30">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="hover:text-[#D4AF37] disabled:opacity-30" title="Move up">▲</button>
                  <button onClick={() => move(idx, +1)} disabled={idx === data.regions.length - 1} className="hover:text-[#D4AF37] disabled:opacity-30" title="Move down">▼</button>
                </div>
                <GripVertical className="w-3.5 h-3.5 text-white/20 shrink-0" />
                <label className="inline-flex items-center gap-1.5 shrink-0">
                  <input type="checkbox" checked={!!r.enabled} onChange={(e) => patchRegion(idx, { enabled: e.target.checked })} className="accent-[#D4AF37] w-4 h-4" data-testid={`footprint-region-toggle-${r.id}`} />
                  <span className={`text-[10px] uppercase tracking-[0.15em] ${r.enabled ? 'text-[#D4AF37]' : 'text-white/40'}`}>{r.enabled ? 'On' : 'Off'}</span>
                </label>
                <input
                  value={r.label || ''}
                  onChange={(e) => patchRegion(idx, { label: e.target.value })}
                  placeholder="Label"
                  className={`flex-1 bg-[#0a1a14] border border-white/10 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#D4AF37]/60 ${r.enabled ? 'text-white' : 'text-white/40'}`}
                  data-testid={`footprint-region-label-${r.id}`}
                />
                <button onClick={() => removeRegion(idx)} className="text-white/30 hover:text-red-400 p-1.5" title="Remove region" data-testid={`footprint-region-remove-${r.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Row 2: subtitle */}
              <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] items-center gap-2">
                <FieldToggle
                  checked={r.show_subtitle}
                  onChange={(e) => patchRegion(idx, { show_subtitle: e.target.checked })}
                  label="Subtitle"
                  testid={`footprint-region-show-subtitle-${r.id}`}
                />
                <input
                  value={r.subtitle || ''}
                  onChange={(e) => patchRegion(idx, { subtitle: e.target.value })}
                  placeholder="e.g. Ghana · Nigeria · Kenya"
                  className="bg-[#0a1a14] border border-white/10 rounded px-2.5 py-1.5 text-[12px] text-white focus:outline-none focus:border-[#D4AF37]/60"
                  data-testid={`footprint-region-subtitle-${r.id}`}
                />
              </div>

              {/* Row 3: phone + email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                  <FieldToggle
                    checked={r.show_phone}
                    onChange={(e) => patchRegion(idx, { show_phone: e.target.checked })}
                    label="Phone"
                    testid={`footprint-region-show-phone-${r.id}`}
                  />
                  <input
                    value={r.phone || ''}
                    onChange={(e) => patchRegion(idx, { phone: e.target.value })}
                    placeholder="+44 ..."
                    className="bg-[#0a1a14] border border-white/10 rounded px-2.5 py-1.5 text-[12px] font-mono text-white focus:outline-none focus:border-[#D4AF37]/60"
                    data-testid={`footprint-region-phone-${r.id}`}
                  />
                </div>
                <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                  <FieldToggle
                    checked={r.show_email}
                    onChange={(e) => patchRegion(idx, { show_email: e.target.checked })}
                    label="Email"
                    testid={`footprint-region-show-email-${r.id}`}
                  />
                  <input
                    value={r.email || ''}
                    onChange={(e) => patchRegion(idx, { email: e.target.value })}
                    placeholder="region@black-starpi.com"
                    className="bg-[#0a1a14] border border-white/10 rounded px-2.5 py-1.5 text-[12px] font-mono text-white focus:outline-none focus:border-[#D4AF37]/60"
                    data-testid={`footprint-region-email-${r.id}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GlobalFootprintTab;
